import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import {
  Observable,
  catchError,
  concatMap,
  endWith,
  from,
  map,
  of,
  switchMap,
  takeUntil,
  toArray,
  withLatestFrom,
} from 'rxjs'

import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

import { AiIntegratorActions } from './integrator.actions'

import { EndpointApiService } from '../../../../integration-designer/services/endpoint.api'
import { SystemApiService } from '../../../../integration-designer/services/system.api'
import { ApplicationDetailsStore } from '../../../../projects/store/application-version-details.store'
// eslint-disable-next-line import/no-cycle
import { AiActions } from '../../../store/ai.actions'
import { AiSelectors } from '../../../store/ai.selectors'
import { AiIntegratorApi, GenerateDatasourcePayload } from '../api/integrator.api'

@Injectable()
export class AiIntegratorEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #integratorApi = inject(AiIntegratorApi)
  #systemApi = inject(SystemApiService)
  #endpointApi = inject(EndpointApiService)
  #applicationDetailsStore = inject(ApplicationDetailsStore)
  #toastMessageService = inject(ToastMessageService)
  #router = inject(Router)

  generateDatasourceStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiIntegratorActions.generateDatasource),
      withLatestFrom(this.#store.select(AiSelectors.sessionId)),
      map(([{ prompt, openapiUrl }, sessionId]) => {
        // Detect if user wants a database vs REST API
        const lowerPrompt = prompt.toLowerCase()
        const isDatabase =
          /\b(database|collection|mongodb|nosql|flowx\s*db|flowx\s*database)\b/.test(lowerPrompt)

        let action: GenerateDatasourcePayload['action'] = 'generate_from_prompt'
        if (openapiUrl) {
          action = 'generate_from_openapi'
        } else if (isDatabase) {
          action = 'generate_database'
        }

        const payload: GenerateDatasourcePayload = {
          question: prompt,
          action,
          openapi_url: openapiUrl,
          session_id: sessionId,
        }
        return payload
      }),
      switchMap((payload) =>
        this.#integratorApi.getSSEGenerateDatasource(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiIntegratorActions.getAnswerEventStream({ response })),
          catchError(({ status }) =>
            of(
              AiActions.getAnswerFailure({
                statusCode: status,
                errorMsg: null,
              })
            )
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      )
    )
  )

  keepDatasourceDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiIntegratorActions.keepDatasourceDraft),
      withLatestFrom(this.#store.select(AiSelectors.generatedDatasource)),
      switchMap(([, generatedDatasource]) => {
        if (!generatedDatasource) {
          this.#toastMessageService.showErrorMessage('No data source to save')
          return of(AiIntegratorActions.keepDatasourceDraftFailure())
        }

        // Detect DB vs REST by checking for operations (DB) vs endpoints (REST)
        if (generatedDatasource.operations) {
          return this.#saveDatabaseDatasource(generatedDatasource)
        }
        return this.#saveDatasource(generatedDatasource)
      })
    )
  )

  editDatasourceStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiIntegratorActions.editDatasource),
      withLatestFrom(this.#store.select(AiSelectors.sessionId)),
      map(([{ prompt, currentSystem }, sessionId]) => {
        const payload: GenerateDatasourcePayload = {
          question: prompt,
          action: 'edit_datasource',
          current_system: currentSystem,
          session_id: sessionId,
        }
        return payload
      }),
      switchMap((payload) =>
        this.#integratorApi.getSSEGenerateDatasource(payload).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          map((response) => AiIntegratorActions.getAnswerEventStream({ response })),
          catchError(({ status }) =>
            of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      )
    )
  )

  keepEditedDatasourceDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiIntegratorActions.keepEditedDatasourceDraft),
      withLatestFrom(
        this.#store.select(AiSelectors.generatedDatasource),
        this.#store.select(AiSelectors.currentSystemContext)
      ),
      switchMap(([, editedSystem, currentSystem]) => {
        if (!editedSystem || !currentSystem) {
          this.#toastMessageService.showErrorMessage('No edited data source to apply')
          return of(AiIntegratorActions.keepDatasourceDraftFailure())
        }
        return this.#applyEdits(currentSystem, editedSystem)
      })
    )
  )

  #applyEdits(currentSystem: any, editedSystem: any): Observable<any> {
    const resourceId = currentSystem.resourceId
    const systemInfo = {
      resourceId,
      name: currentSystem.name,
      resourceDefinitionId: currentSystem.resourceDefinitionId,
    } as any

    this.#toastMessageService.showSuccessMessage('Applying changes...')

    // Match existing endpoints by (httpMethod, url)
    const currentEndpoints: any[] = currentSystem.endpoints || []
    const editedEndpoints: any[] = editedSystem.endpoints || []

    const currentKeys = new Set(currentEndpoints.map((ep: any) => `${ep.httpMethod}:${ep.url}`))
    const editedKeys = new Set(editedEndpoints.map((ep: any) => `${ep.httpMethod}:${ep.url}`))

    const newEndpoints = editedEndpoints.filter(
      (ep: any) => !currentKeys.has(`${ep.httpMethod}:${ep.url}`)
    )
    const removedEndpoints = currentEndpoints.filter(
      (ep: any) => !editedKeys.has(`${ep.httpMethod}:${ep.url}`)
    )

    // Auth change
    const authChanged = editedSystem.authorization?.type !== currentSystem.authorization?.type
    const authObs$ = authChanged
      ? (this.#systemApi as any)
          .setAuthorization(
            currentSystem as any,
            { authorization: editedSystem.authorization } as any
          )
          .pipe(
            catchError((err) => {
              console.log('[Integrator] Failed to update auth:', err)
              return of(null)
            })
          )
      : of(null)

    // Delete removed endpoints
    const deleteObs$ =
      removedEndpoints.length > 0
        ? from(removedEndpoints).pipe(
            concatMap((ep: any) =>
              this.#systemApi.deleteEndpoint(systemInfo, ep.id || ep.flowxUuid).pipe(
                catchError((err) => {
                  console.log('[Integrator] Failed to delete endpoint:', err)
                  return of(null)
                })
              )
            ),
            toArray()
          )
        : of([])

    return authObs$.pipe(
      switchMap(() => deleteObs$),
      switchMap(() => {
        if (newEndpoints.length === 0) {
          this.#toastMessageService.showSuccessMessage('Changes applied successfully')
          // Reload the page
          const currentUrl = this.#router.url
          this.#router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
            this.#router.navigateByUrl(currentUrl)
          })
          return of(
            AiIntegratorActions.keepEditedDatasourceDraftSuccess({ response: editedSystem })
          )
        }

        // Create new endpoints with full chain
        return from(newEndpoints).pipe(
          concatMap((endpoint: any) =>
            this.#systemApi
              .createEndpoint(
                currentSystem as any,
                {
                  name: endpoint.name,
                  httpMethod: endpoint.httpMethod,
                  description: endpoint.description || '',
                } as any
              )
              .pipe(
                switchMap((epResponse: any) => {
                  const createdEp = epResponse?.resourceContent || epResponse
                  if (!createdEp?.id) {
                    return of(null)
                  }

                  const updateUrl$ = endpoint.url
                    ? this.#systemApi
                        .updateEndpointGeneral(
                          currentSystem as any,
                          {
                            id: createdEp.id,
                            name: endpoint.name,
                            httpMethod: endpoint.httpMethod,
                            description: endpoint.description || '',
                            url: endpoint.url,
                          } as any
                        )
                        .pipe(catchError(() => of(null)))
                    : of(null)

                  const allParams = [
                    ...(endpoint.pathParameters || []).map((p: any) => ({
                      ...p,
                      paramType: 'PATH',
                    })),
                    ...(endpoint.queryParameters || []).map((p: any) => ({
                      ...p,
                      paramType: 'QUERY',
                    })),
                    ...(endpoint.headers || []).map((p: any) => ({ ...p, paramType: 'HEADER' })),
                  ]

                  return updateUrl$.pipe(
                    switchMap(() => {
                      if (allParams.length === 0) {
                        return of(null)
                      }
                      return from(allParams).pipe(
                        concatMap((param: any) =>
                          this.#endpointApi
                            .createParameter(systemInfo, createdEp.id, {
                              key: param.key,
                              defaultValue: param.defaultValue || '',
                              description: param.description || '',
                              required: param.required || false,
                              paramType: param.paramType,
                            } as any)
                            .pipe(catchError(() => of(null)))
                        ),
                        toArray()
                      )
                    }),
                    switchMap(() => {
                      const rb = endpoint.requestBody
                      if (!rb?.jsonPayload) {
                        return of(null)
                      }
                      return this.#endpointApi
                        .updateJsonParam(systemInfo, createdEp.id, rb.jsonPayload)
                        .pipe(catchError(() => of(null)))
                    }),
                    switchMap(() => {
                      const responses = endpoint.responses || []
                      if (responses.length === 0) {
                        return of(null)
                      }
                      return from(responses).pipe(
                        concatMap((resp: any) =>
                          this.#endpointApi
                            .addResponse(systemInfo, createdEp.id, {
                              code: resp.code || '200',
                              body:
                                typeof resp.body === 'string'
                                  ? JSON.parse(resp.body || '{}')
                                  : resp.body || {},
                              headers: [],
                            } as any)
                            .pipe(catchError(() => of(null)))
                        ),
                        toArray()
                      )
                    })
                  )
                }),
                catchError((err) => {
                  console.log('[Integrator] Failed to create endpoint:', err)
                  return of(null)
                })
              )
          ),
          toArray(),
          switchMap(() => {
            this.#toastMessageService.showSuccessMessage(
              `Applied changes: ${newEndpoints.length} new endpoints, ${removedEndpoints.length} removed`
            )
            const currentUrl = this.#router.url
            this.#router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
              this.#router.navigateByUrl(currentUrl)
            })
            return of(
              AiIntegratorActions.keepEditedDatasourceDraftSuccess({ response: editedSystem })
            )
          })
        )
      }),
      catchError((err) => {
        console.log('[Integrator] Failed to apply edits:', err)
        this.#toastMessageService.showErrorMessage('Failed to apply changes')
        return of(AiIntegratorActions.keepDatasourceDraftFailure())
      })
    )
  }

  #saveDatasource(system: any): Observable<any> {
    const createPayload = {
      type: 'REST' as const,
      name: system.name,
      code: system.code,
      description: system.description,
      baseUrl: system.baseUrl,
      authorization: system.authorization || { type: 'NO_AUTH' },
    }

    this.#toastMessageService.showSuccessMessage(`Creating data source "${system.name}"...`)

    return this.#systemApi.createSystem(createPayload as any).pipe(
      switchMap((response: any) => {
        const createdSystem = response?.resourceContent || response
        const resourceId = createdSystem?.resourceId

        if (!resourceId) {
          this.#toastMessageService.showErrorMessage(
            'Failed to create data source — no resource ID returned'
          )
          return of(AiIntegratorActions.keepDatasourceDraftFailure())
        }

        // Set authorization if not NO_AUTH
        const authObs =
          system.authorization?.type && system.authorization.type !== 'NO_AUTH'
            ? (this.#systemApi as any)
                .setAuthorization(
                  createdSystem as any,
                  {
                    authorization: system.authorization,
                  } as any
                )
                .pipe(
                  catchError((err) => {
                    console.log('[Integrator] Failed to set auth:', err)
                    return of(null)
                  })
                )
            : of(null)

        return authObs.pipe(
          switchMap(() => {
            const endpoints = system.endpoints || []
            if (endpoints.length === 0) {
              this.#toastMessageService.showSuccessMessage(`Data source "${system.name}" created`)
              return of(AiIntegratorActions.keepDatasourceDraftSuccess({ response: createdSystem }))
            }

            // Create each endpoint, then update its URL path
            let createdCount = 0
            return from(endpoints).pipe(
              concatMap((endpoint: any) =>
                this.#systemApi
                  .createEndpoint(
                    createdSystem as any,
                    {
                      name: endpoint.name,
                      httpMethod: endpoint.httpMethod,
                      description: endpoint.description || '',
                    } as any
                  )
                  .pipe(
                    switchMap((epResponse: any) => {
                      const createdEp = epResponse?.resourceContent || epResponse
                      createdCount++
                      if (!createdEp?.id) {
                        return of(null)
                      }

                      const systemInfo = { resourceId: createdSystem.resourceId } as any

                      // Update endpoint with URL path
                      const updateUrl$ = endpoint.url
                        ? this.#systemApi
                            .updateEndpointGeneral(
                              createdSystem as any,
                              {
                                id: createdEp.id,
                                name: endpoint.name,
                                httpMethod: endpoint.httpMethod,
                                description: endpoint.description || '',
                                url: endpoint.url,
                              } as any
                            )
                            .pipe(catchError(() => of(null)))
                        : of(null)

                      // Collect all parameters (path, query, headers)
                      const allParams = [
                        ...(endpoint.pathParameters || []).map((p: any) => ({
                          ...p,
                          paramType: 'PATH',
                        })),
                        ...(endpoint.queryParameters || []).map((p: any) => ({
                          ...p,
                          paramType: 'QUERY',
                        })),
                        ...(endpoint.headers || []).map((p: any) => ({
                          ...p,
                          paramType: 'HEADER',
                        })),
                      ]

                      // Chain: set URL → create params → set request body → add responses
                      return updateUrl$.pipe(
                        // Step 4: Create parameters
                        switchMap(() => {
                          if (allParams.length === 0) {
                            return of(null)
                          }
                          return from(allParams).pipe(
                            concatMap((param: any) =>
                              this.#endpointApi
                                .createParameter(systemInfo, createdEp.id, {
                                  key: param.key,
                                  defaultValue: param.defaultValue || '',
                                  description: param.description || '',
                                  required: param.required || false,
                                  paramType: param.paramType,
                                } as any)
                                .pipe(catchError(() => of(null)))
                            ),
                            toArray()
                          )
                        }),
                        // Step 5: Set request body (POST/PUT/PATCH only)
                        switchMap(() => {
                          const rb = endpoint.requestBody
                          if (!rb?.jsonPayload) {
                            return of(null)
                          }
                          return this.#endpointApi
                            .updateJsonParam(systemInfo, createdEp.id, rb.jsonPayload)
                            .pipe(catchError(() => of(null)))
                        }),
                        // Step 6: Add responses
                        switchMap(() => {
                          const responses = endpoint.responses || []
                          if (responses.length === 0) {
                            return of(null)
                          }
                          return from(responses).pipe(
                            concatMap((resp: any) =>
                              this.#endpointApi
                                .addResponse(systemInfo, createdEp.id, {
                                  code: resp.code || '200',
                                  body:
                                    typeof resp.body === 'string'
                                      ? JSON.parse(resp.body || '{}')
                                      : resp.body || {},
                                  headers: [],
                                } as any)
                                .pipe(catchError(() => of(null)))
                            ),
                            toArray()
                          )
                        })
                      )
                    }),
                    catchError((err) => {
                      createdCount++
                      console.log(`[Integrator] Failed to create endpoint ${endpoint.name}:`, err)
                      return of(null)
                    })
                  )
              ),
              toArray(),
              switchMap(() => {
                this.#toastMessageService.showSuccessMessage(
                  `Data source "${system.name}" created with ${createdCount} endpoints`
                )

                // Navigate using relative path from current URL
                const currentUrl = this.#router.url
                const basePath = currentUrl.replace(/\/integration-designer.*$/, '')
                this.#router.navigateByUrl(
                  `${basePath}/integration-designer/data-sources/${resourceId}`
                )

                return of(
                  AiIntegratorActions.keepDatasourceDraftSuccess({ response: createdSystem })
                )
              })
            )
          })
        )
      }),
      catchError((err) => {
        console.log('[Integrator] Failed to create data source:', err)
        this.#toastMessageService.showErrorMessage('Failed to create data source')
        return of(AiIntegratorActions.keepDatasourceDraftFailure())
      })
    )
  }

  #saveDatabaseDatasource(db: any): Observable<any> {
    // FlowX Database (NOSQL_DB) requires projectDataTypeForSchema
    this.#toastMessageService.showSuccessMessage(`Creating FlowX Database "${db.name}"...`)

    // Try to find a data type from the project to use as schema
    const dataTypes = this.#applicationDetailsStore.dataTypes?.()?.values
    let firstDataType: any = null
    if (dataTypes && Array.isArray(dataTypes)) {
      for (const group of dataTypes) {
        if (group?.items?.length > 0) {
          firstDataType = group.items[0]
          break
        }
      }
    }

    const createPayload = {
      type: 'NOSQL_DB' as const,
      name: db.name,
      code: db.name?.substring(0, 4)?.toUpperCase() || 'DB01',
      description: db.description || '',
      partitionKey: db.partitionKey || '_id',
      ...(firstDataType
        ? {
            projectDataTypeForSchema: {
              appId: firstDataType.appId || this.#applicationDetailsStore.appId?.() || '',
              resourceDefinitionId: firstDataType.resourceDefinitionId,
            },
          }
        : {}),
    } as any

    return this.#systemApi.createSystem(createPayload as any).pipe(
      switchMap((response: any) => {
        const createdSystem = response?.resourceContent || response
        const resourceId = createdSystem?.resourceId

        if (!resourceId) {
          this.#toastMessageService.showErrorMessage(
            'Failed to create database — no resource ID returned'
          )
          return of(AiIntegratorActions.keepDatasourceDraftFailure())
        }

        const operations: any[] = db.operations || []
        const indexes: any[] = db.indexes || []

        if (operations.length === 0 && indexes.length === 0) {
          this.#toastMessageService.showSuccessMessage(`Database "${db.name}" created`)
          return of(AiIntegratorActions.keepDatasourceDraftSuccess({ response: createdSystem }))
        }

        // Create operations sequentially: first create, then update with options/params
        return from(operations).pipe(
          concatMap((op: any) =>
            this.#systemApi
              .createOperation(
                createdSystem as any,
                {
                  name: op.name,
                  type: op.type,
                  description: op.description || '',
                } as any
              )
              .pipe(
                switchMap((opResponse: any) => {
                  const createdOp = opResponse?.resourceContent || opResponse
                  if (!createdOp?.id) {
                    return of(null)
                  }

                  // Update operation with options, payload, and params
                  const hasDetails = op.options || op.payload || (op.params && op.params.length > 0)
                  if (!hasDetails) {
                    return of(null)
                  }

                  return this.#systemApi
                    .updateOperation(
                      createdSystem as any,
                      {
                        id: createdOp.id,
                        name: op.name,
                        description: op.description || '',
                        options: op.options || {},
                        payload: op.payload || '',
                        params: (op.params || []).map((p: any) => ({
                          key: p.key,
                          defaultValue: p.defaultValue || '',
                          type: p.type || 'STRING',
                          description: p.description || '',
                          required: p.required || false,
                        })),
                      } as any
                    )
                    .pipe(catchError(() => of(null)))
                }),
                catchError((err) => {
                  console.log(`[Integrator] Failed to create operation ${op.name}:`, err)
                  return of(null)
                })
              )
          ),
          toArray(),
          // Create indexes
          switchMap(() => {
            if (indexes.length === 0) {
              return of([])
            }
            return from(indexes).pipe(
              concatMap((idx: any) =>
                this.#systemApi
                  .createIndex(
                    createdSystem as any,
                    {
                      name: idx.name,
                      type: idx.type || 'SIMPLE',
                      attributes: (idx.attributes || []).map((a: any) => ({
                        name: a.name,
                        direction: a.direction || 'ASC',
                      })),
                    } as any
                  )
                  .pipe(catchError(() => of(null)))
              ),
              toArray()
            )
          }),
          switchMap(() => {
            this.#toastMessageService.showSuccessMessage(
              `Database "${db.name}" created with ${operations.length} operations and ${indexes.length} indexes`
            )

            const currentUrl = this.#router.url
            const basePath = currentUrl.replace(/\/integration-designer.*$/, '')
            this.#router.navigateByUrl(
              `${basePath}/integration-designer/data-sources/${resourceId}`
            )

            return of(AiIntegratorActions.keepDatasourceDraftSuccess({ response: createdSystem }))
          })
        )
      }),
      catchError((err: any) => {
        console.log('[Integrator] Failed to create database:', err)
        const errorMsg =
          err?.error?.defaultMessage || err?.error?.title || 'Failed to create database'
        if (errorMsg.includes('Schema not set')) {
          this.#toastMessageService.showErrorMessage(
            'FlowX Database requires a Schema Definition (Project Data Type). Please create a data type first in Project Data Model, then create the database manually and link the schema.'
          )
        } else {
          this.#toastMessageService.showErrorMessage(errorMsg)
        }
        return of(AiIntegratorActions.keepDatasourceDraftFailure())
      })
    )
  }
}
